import { events as eventConstants } from '../constants/events';
import { getUserCookiePreferences } from '../utils/optanon';
import { generateGtmNoscriptContents, generateGtmScriptContents } from '../utils/scriptUtils';
import Tracker from './Tracker';
import type { initTracker } from './Tracker';

export interface resultType {
  success: boolean | Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/ban-types
  data: object;
}

export default class Gtm extends Tracker {
  constructor(args: initTracker) {
    super({ ...args, type: 'gtm' });
  }

  renderHtmlInBody = () => {
    const { id } = this;

    return `
            <!-- Google Tag Manager (noscript) -->
            <noscript>${generateGtmNoscriptContents(id)}</noscript>
            <!-- End Google Tag Manager (noscript) -->
        `;
  };

  renderHtmlInHead = () => {
    const { id } = this;

    return `
            <!-- Google Tag Manager -->
            <script>${generateGtmScriptContents(id)}</script>
            <!-- End Google Tag Manager -->
        `;
  };

  stopDisabledTracker = () => {
    const { isFunctionalCookieBlocked } = getUserCookiePreferences();

    this.logger &&
      this.logger.debug(
        {
          isFunctionalCookieBlocked,
        },
        'checking for disabled trackers',
      );

    // this probably doesn't do anything
    // i don't know how we can block a UA tag from reporting if it is loaded via GTM
    if (isFunctionalCookieBlocked) {
      this.logger &&
        this.logger.debug(
          {
            id: this.id,
          },
          'disabling tracker',
        );
      // @ts-ignore window has some read-only properties
      window[`ga-disable-${this.id}`] = true;
    }
  };

  _pushToDataLayer(data: unknown): void {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push(data);
    }
  }

  trackPageView = ({ path = '', ...data } = {}, cb: (s: string) => void): resultType => {
    const { id, drymode } = this;

    this.logger &&
      this.logger.debug(
        {
          id,
          path,
          ...data,
        },
        'trackPageView',
      );

    if (!drymode) {
      this._pushToDataLayer({
        event: eventConstants.pageView,
        comet_page_path: path,
        ...data,
      });

      if (cb) {
        cb(id);
      }
    }

    return this.result(true, {
      event: eventConstants.pageView,
      comet_page_path: path,
      ...data,
    });
  };

  trackEvent = (
    {
      category,
      action,
      label,
      value,
      clickstreamEvent,
      ...rest
    }: Record<string, unknown> & { clickstreamEvent?: { envelope?: { event_type_id?: string } } },
    cb: (s: string) => void,
  ): resultType | undefined => {
    const { id, drymode, name } = this;

    this.logger &&
      this.logger.debug(
        {
          id,
          event: eventConstants.event,
          comet_ga_category: category,
          comet_ga_action: action,
          comet_ga_label: label,
          comet_ga_value: value,
          name,
          ...rest,
        },
        'trackEvent',
      );

    if (!drymode) {
      this._pushToDataLayer({
        event: eventConstants.event,
        comet_ga_category: category,
        comet_ga_action: action,
        comet_ga_label: label,
        comet_ga_value: value,
        ...rest,
      });

      if (cb) {
        cb(id);
      }
    }

    return this.result(true, {
      event: eventConstants.event,
      comet_ga_category: category,
      comet_ga_action: action,
      comet_ga_label: label,
      comet_ga_value: value,
      ...rest,
    });
  };

  setCustomDimensions = ({ name, value }: Record<string, unknown>, cb: (s: string) => void): resultType => {
    this.logger &&
      this.logger.debug(
        'you must configure your custom dimensions in GTM for comet events. ' +
          'Add the dimension index and the dataLayer variable pushed here to the ' +
          'tag configuration in GTM to see the data passed through to UA.',
      );

    const { id, drymode } = this;

    this.logger && this.logger.debug({ name, value }, 'setCustomDimensions - %O');

    if (!drymode) {
      this._pushToDataLayer({
        [name as string]: value,
      });

      if (cb) {
        cb(id);
      }
    }

    return this.result(true, {
      name,
      value,
    });
  };
}
